"use client";

import { Input } from "@cmp-src/primitives/input";
import { cn } from "@gettactic/helpers";
import { Check, ChevronDown, ChevronRight, Minus } from "lucide-react";
import React, { useMemo, useState } from "react";
import { type LocationNode, leafIds } from "../lib/locationTree";

interface LocationTreeProps {
  nodes: LocationNode[];
  /** The picked leaf ids; a single-pick tree holds at most one. */
  selected: string[];
  /** Every picked leaf id (multi), or the one id just picked (single). */
  onChange: (ids: string[]) => void;
  /** Tick many leaves with tri-state boxes, or click one leaf to pick it. */
  mode: "multi" | "single";
  searchPlaceholder?: string;
  /** Shown when there is nothing to list at all, before any search. */
  emptyText?: string;
}

type BoxState = "full" | "part" | "none";

interface TriStateBoxProps {
  state: BoxState;
  label: string;
  onToggle: () => void;
}

/** A real checkbox, drawn like the picker's others, with a third "some" state. */
const TriStateBox: React.FC<TriStateBoxProps> = ({
  state,
  label,
  onToggle
}) => (
  <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
    <input
      type="checkbox"
      aria-label={label}
      checked={state === "full"}
      ref={(element) => {
        if (element) {
          element.indeterminate = state === "part";
        }
      }}
      onChange={onToggle}
      className="h-4 w-4 cursor-pointer appearance-none rounded-md border border-neutral-400 bg-white checked:border-black checked:bg-black indeterminate:border-black indeterminate:bg-black"
    />
    {state === "none" ? null : (
      <span className="pointer-events-none absolute text-white">
        {state === "full" ? (
          <Check className="h-3 w-3" strokeWidth={3} />
        ) : (
          <Minus className="h-3 w-3" strokeWidth={3} />
        )}
      </span>
    )}
  </span>
);

/** Nodes whose label matches the needle, or that hold one that does. */
const filterNodes = (nodes: LocationNode[], needle: string): LocationNode[] =>
  nodes.flatMap((n): LocationNode[] => {
    if (n.label.toLowerCase().includes(needle)) {
      return [n];
    }
    const children = filterNodes(n.children, needle);
    return children.length > 0 ? [{ ...n, children }] : [];
  });

/** The nodes on a path down to a selected leaf, so the tree opens on them. */
const pathsTo = (nodes: LocationNode[], selected: Set<string>): string[] =>
  nodes.flatMap((n) =>
    n.children.length > 0 && leafIds(n).some((id) => selected.has(id))
      ? [n.id, ...pathsTo(n.children, selected)]
      : []
  );

const toggled = (set: Set<string>, id: string): Set<string> => {
  const next = new Set(set);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

/**
 * Divisions, offices and floors as an expandable tree with a search box.
 * Ticking a division picks every office below it; a single-pick tree
 * expands on a division and picks on a leaf.
 */
export const LocationTree: React.FC<LocationTreeProps> = ({
  nodes,
  selected,
  onChange,
  mode,
  searchPlaceholder = "Search…",
  emptyText = "Nothing to pick."
}) => {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  // The top level is open, and so is whatever leads to a current pick; the
  // reader's clicks flip nodes from that. Both are kept apart so a tree
  // that fills in later (divisions arriving after the offices) opens too.
  const openByDefault = useMemo(
    () => new Set([...nodes.map((n) => n.id), ...pathsTo(nodes, selectedSet)]),
    [nodes, selectedSet]
  );
  const [flipped, setFlipped] = useState<Set<string>>(() => new Set());
  const isExpanded = (id: string) => openByDefault.has(id) !== flipped.has(id);
  const flip = (id: string) => setFlipped((prev) => toggled(prev, id));
  const needle = search.trim().toLowerCase();
  const visible = useMemo(
    () => (needle ? filterNodes(nodes, needle) : nodes),
    [nodes, needle]
  );

  const toggleNode = (n: LocationNode) => {
    if (mode === "single") {
      onChange([n.id]);
      return;
    }
    const leaves = leafIds(n);
    const next = new Set(selected);
    const hasAll = leaves.every((id) => next.has(id));
    for (const id of leaves) {
      if (hasAll) {
        next.delete(id);
      } else {
        next.add(id);
      }
    }
    onChange([...next]);
  };

  const row = (n: LocationNode, depth: number): React.ReactNode => {
    const hasChildren = n.children.length > 0;
    const isOpen = needle.length > 0 || isExpanded(n.id);
    const leaves = leafIds(n);
    const picked = leaves.filter((id) => selectedSet.has(id)).length;
    const state: BoxState =
      picked === 0 ? "none" : picked === leaves.length ? "full" : "part";
    const isPickable = mode === "multi" || !hasChildren;
    return (
      <React.Fragment key={n.id}>
        <div
          className="flex items-center gap-1.5 rounded py-1 pr-2 text-[13px] hover:bg-neutral-50"
          // Indent follows the depth, which no fixed class can express.
          style={{ paddingLeft: 4 + depth * 16 }}
        >
          {hasChildren ? (
            <button
              type="button"
              aria-label={isOpen ? "Collapse" : "Expand"}
              onClick={() => flip(n.id)}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-neutral-400 hover:text-neutral-900"
            >
              {isOpen ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="h-5 w-5 shrink-0" aria-hidden />
          )}
          {mode === "multi" ? (
            <TriStateBox
              state={state}
              label={n.label}
              onToggle={() => toggleNode(n)}
            />
          ) : null}
          <button
            type="button"
            onClick={() => (isPickable ? toggleNode(n) : flip(n.id))}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <span
              className={cn(
                "truncate",
                n.kind === "division"
                  ? "font-medium text-neutral-800"
                  : "text-neutral-900"
              )}
            >
              {n.label}
            </span>
            {hasChildren ? (
              <span className="shrink-0 text-[11px] text-neutral-400 tabular-nums">
                {leaves.length}
              </span>
            ) : null}
            {mode === "single" && !hasChildren && state === "full" ? (
              <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" />
            ) : null}
          </button>
        </div>
        {hasChildren && isOpen
          ? n.children.map((child) => row(child, depth + 1))
          : null}
      </React.Fragment>
    );
  };

  return (
    <>
      <div className="border-neutral-200 border-b p-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={searchPlaceholder}
          autoFocus
        />
      </div>
      <div className="max-h-64 overflow-y-auto p-1">
        {visible.length === 0 ? (
          <p className="px-2 py-3 text-[12px] text-neutral-500">
            {nodes.length === 0 ? emptyText : "Nothing matches."}
          </p>
        ) : (
          visible.map((n) => row(n, 0))
        )}
      </div>
    </>
  );
};
